import {
  EarningsPeriod,
  EarningsPeriods,
  ScreenerFilter,
  ScreenerFilters,
} from '@api';
import { RowSelectionState, SortingState } from '@tanstack/react-table';
import {
  EarningFilterGroupKeys,
  FilterGroups,
  FilterState,
  SharedFilterState,
} from '@terminal/hooks/use-filter-actions/useFilterActions';
import { create } from '@terminal/utils/store/createStore';

import { filterEarnings, getSelectedRowKeys } from '../utils/utils';

const DEFAULT_STATE = {
  earningExchange: '',
  activeModalFilter: null,
  activeSortingState: [],
  defaultFilterOption: null,
  earningsData: [],
  filters: [],
  isViewSelectedOnly: true,
  rowSelection: {},
  rowSelectionByFilters: {},
  rowSelectionByManualEdit: {},
};

type InitStoreProps = {
  data: EarningsPeriods;
  earningExchange: string;
  filters: ScreenerFilters;
};

type EarningsStore = {
  activeModalFilter: ScreenerFilter | null;
  activeSortingState: SortingState;
  earningsData: EarningsPeriods;
  earningExchange: string;
  isViewSelectedOnly: boolean;
  rowSelection: RowSelectionState;
  rowSelectionByFilters: RowSelectionState;
  rowSelectionByManualEdit: RowSelectionState;
  setActiveModalFilter: (filter: ScreenerFilter | null) => void;
  setActiveSortingState: (sortingState: SortingState) => void;
  initStore: ({ data, earningExchange, filters }: InitStoreProps) => void;
  resetStore: () => void;
  getIsAllRowsSelected: () => boolean;
  getIsSomeRowsSelected: () => boolean;
  toggleAllRowsSelection: (selected: boolean) => void;
  updateRowSelection: (key: string, selected: boolean) => void;
  selectRowsByEpisodeDates: (episodeDates: string[]) => void;
  setIsViewSelectedOnly: (isSelected: boolean) => void;
  resetSelectionByManualEdit: () => void;
  getIsRowManualChanged: (key: string) => boolean;
  getIsRowMatchingFilters: (key: string) => boolean;
  onFilterGroupsChange: (
    filterGroups: FilterGroups<EarningFilterGroupKeys>,
    group?: EarningFilterGroupKeys
  ) => void;
  resetAllFilterOptions: () => void;
} & SharedFilterState<EarningFilterGroupKeys>;

const toUtcDay = (date: string) => new Date(date).toISOString().slice(0, 10);

const getSelectedRowFromEarningsData = (data: EarningsPeriods) =>
  data.reduce<RowSelectionState>((selectedRows, earning: EarningsPeriod) => {
    selectedRows[earning.reporting_date] = true;
    return selectedRows;
  }, {});

export const useEarningsStore = create<EarningsStore>((set, get) => ({
  filterGroups: {} as FilterGroups<EarningFilterGroupKeys>,
  initStore: ({ data, earningExchange, filters }: InitStoreProps) => {
    const selectedRows = getSelectedRowFromEarningsData(data);

    const filterState: FilterState = {
      allFilters: filters,
      activeFilterOptions: [],
      activeFilters: filters.filter(f => f.visible),
    };

    set({
      earningsData: data,
      earningExchange,
      rowSelection: selectedRows,
      rowSelectionByFilters: selectedRows,
      filterGroups: {
        EARNINGS_FILTERS: filterState,
      },
    });
  },
  resetStore: () => {
    set(DEFAULT_STATE);
  },
  ...DEFAULT_STATE,
  setActiveModalFilter: (filter: ScreenerFilter | null) =>
    set({ activeModalFilter: filter }),
  resetSelectionByManualEdit: () => {
    const { rowSelectionByFilters } = get();
    set({
      rowSelectionByManualEdit: {},
      rowSelection: { ...rowSelectionByFilters },
    });
  },
  setIsViewSelectedOnly: (isSelected: boolean) =>
    set({ isViewSelectedOnly: isSelected }),
  setActiveSortingState: sortingState =>
    set({ activeSortingState: sortingState }),
  toggleAllRowsSelection: (selected: boolean) => {
    const { updateRowSelection, earningsData } = get();

    for (const earnings of earningsData) {
      updateRowSelection(earnings.reporting_date, selected);
    }
  },
  updateRowSelection: (rowId: string, selected: boolean) => {
    const { rowSelection, rowSelectionByFilters, rowSelectionByManualEdit } =
      get();

    const updatedRowSelection = { ...rowSelection };
    if (selected) {
      updatedRowSelection[rowId] = true;
    } else {
      delete updatedRowSelection[rowId];
    }
    const updatedRowSelectionByManualEdit = { ...rowSelectionByManualEdit };

    if (!!rowSelectionByFilters[rowId] === selected) {
      delete updatedRowSelectionByManualEdit[rowId];
    } else if (selected) {
      updatedRowSelectionByManualEdit[rowId] = true;
    } else {
      delete updatedRowSelectionByManualEdit[rowId];
    }

    set({
      rowSelection: updatedRowSelection,
      rowSelectionByManualEdit: updatedRowSelectionByManualEdit,
    });
  },
  selectRowsByEpisodeDates: (episodeDates: string[]) => {
    const { earningsData } = get();

    const episodeDays = new Set(episodeDates.map(toUtcDay));
    const selectedRows = earningsData.reduce<RowSelectionState>(
      (result, earning) => {
        if (episodeDays.has(toUtcDay(earning.reporting_date))) {
          result[earning.reporting_date] = true;
        }

        return result;
      },
      {}
    );

    set({
      rowSelection: selectedRows,
      rowSelectionByManualEdit: {},
    });
  },
  getIsAllRowsSelected: () => {
    const { rowSelection, earningsData } = get();
    const selectedRowKeys = getSelectedRowKeys(rowSelection);
    return earningsData.length === selectedRowKeys.length;
  },
  getIsSomeRowsSelected: () => {
    const { rowSelection } = get();
    const selectedRowKeys = getSelectedRowKeys(rowSelection);
    return selectedRowKeys.length > 0;
  },
  getIsRowManualChanged: (rowId: string) => {
    const { rowSelectionByManualEdit } = get();
    return !!rowSelectionByManualEdit[rowId];
  },
  getIsRowMatchingFilters: (rowId: string) => {
    const { rowSelectionByFilters } = get();
    return rowSelectionByFilters[rowId];
  },
  onFilterGroupsChange: newFilterGroups => {
    const { earningsData } = get();
    const activeFilterOptions =
      newFilterGroups.EARNINGS_FILTERS.activeFilterOptions;
    const filteredEarnings = filterEarnings(activeFilterOptions, earningsData);
    const selection = getSelectedRowFromEarningsData(filteredEarnings);

    set({
      rowSelectionByManualEdit: {},
      rowSelection: selection,
      rowSelectionByFilters: selection,
      filterGroups: newFilterGroups,
    });
  },
  resetAllFilterOptions: () => {
    const { onFilterGroupsChange, filterGroups } = get();
    onFilterGroupsChange({
      EARNINGS_FILTERS: {
        ...filterGroups.EARNINGS_FILTERS,
        activeFilterOptions: [],
      },
    });
  },
}));
